import { useEffect, useState } from "react";
import { api, media } from "@/lib/api";
import { useApp } from "@/lib/store";
import { toast } from "sonner";
import { X, ShieldCheck, FlaskConical, ArrowRight, Plus, Minus, Heart, MessageSquare } from "lucide-react";

export default function InspectPanel({ slug, onClose, onAsk, track, room, onFav, faved, onOpenCart }) {
  const [p, setP] = useState(null);
  const [variant, setVariant] = useState(null);
  const [qty, setQty] = useState(1);
  const { addToCart } = useApp();

  useEffect(() => {
    setP(null); setVariant(null); setQty(1);
    api.get(`/vr/product/${slug}`).then(({ data }) => {
      setP(data);
      if (data.variants?.length) setVariant(data.variants[0]);
    }).catch(() => {});
  }, [slug]);

  if (!p) return (
    <Shell onClose={onClose}><div className="p-10 text-center text-cyan-300 font-mono text-sm animate-pulse">LOADING PRODUCT…</div></Shell>
  );

  const tier = (p.bulk_tiers || []).slice().reverse().find((t) => qty >= (t.min_qty || 0));
  const disc = tier?.discount_pct || 0;
  const unit = variant?.price || p.from_price || 0;
  const subtotal = unit * qty;
  const discounted = subtotal * (1 - disc / 100);

  const add = () => {
    if (!variant) return;
    addToCart(variant, p, qty);
    track && track("add_to_cart", { product_slug: slug, room, meta: { sku: variant.sku, qty } });
    toast.success(`${p.name} · ${variant.label} added to Lab Cart`);
    onOpenCart && onOpenCart();
  };

  return (
    <Shell onClose={onClose}>
      <div className="grid md:grid-cols-2 gap-6 p-6">
        <div className="rounded-2xl bg-gradient-to-b from-cyan-500/10 via-black/40 to-black/60 border border-cyan-400/20 h-[320px] md:h-[420px] overflow-hidden relative flex items-center justify-center p-5">
          <img src={media(p.image)} alt={p.name} data-testid="vr-inspect-image" className="max-h-full max-w-full object-contain transition-transform duration-500 hover:scale-105" style={{ filter: "drop-shadow(0 0 30px rgba(56,225,255,0.3))" }} />
          <div className="absolute top-3 left-3 text-[10px] font-mono text-cyan-300/70 tracking-widest">CG LABZ · BRANDED VIAL</div>
        </div>
        <div className="flex flex-col">
          <div className="flex items-center gap-2 text-[11px] font-mono text-cyan-300 tracking-widest uppercase">
            <FlaskConical size={13} /> {(p.categories || [])[0] || "Peptide"}
          </div>
          <h2 className="text-3xl font-semibold mt-1 text-white" data-testid="vr-inspect-name">{p.name}</h2>
          <p className="text-sm text-slate-400 mt-2 line-clamp-3">{p.tagline || p.description}</p>

          {p.variants?.length > 0 && (
            <div className="mt-4">
              <div className="text-[11px] font-mono text-slate-400 mb-2">SELECT STRENGTH</div>
              <div className="flex flex-wrap gap-2">
                {p.variants.map((v) => (
                  <button key={v.sku} data-testid={`vr-variant-${v.sku}`}
                    onClick={() => setVariant(v)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-mono border transition ${variant?.sku === v.sku ? "bg-cyan-400 text-black border-cyan-400" : "border-white/15 text-slate-200 hover:border-cyan-400/50"}`}>
                    {v.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4 flex items-end gap-4">
            <div>
              <div className="text-[11px] font-mono text-slate-400">PRICE</div>
              <div className="text-2xl font-semibold text-white">${unit.toFixed(2)}</div>
            </div>
            {variant?.price_per_mg ? (
              <div className="text-xs text-slate-400 font-mono mb-1">${variant.price_per_mg.toFixed(2)}/mg</div>
            ) : null}
            {variant?.sku && <div className="text-[10px] text-slate-500 font-mono mb-1">SKU {variant.sku}</div>}
          </div>

          {p.bulk_tiers?.length > 0 && (
            <div className="mt-3 text-[11px] font-mono text-mint-300 text-emerald-300">
              Volume pricing active · buy more to save up to {Math.max(...p.bulk_tiers.map((t) => t.discount_pct || 0))}%
              {disc > 0 && <span className="text-emerald-400"> · {disc}% applied</span>}
            </div>
          )}

          <div className="mt-4 flex items-center gap-3">
            <div className="flex items-center border border-white/15 rounded-lg">
              <button className="p-2 text-slate-300" onClick={() => setQty((q) => Math.max(1, q - 1))}><Minus size={14} /></button>
              <span className="w-10 text-center font-mono text-sm" data-testid="vr-qty">{qty}</span>
              <button className="p-2 text-slate-300" onClick={() => setQty((q) => q + 1)}><Plus size={14} /></button>
            </div>
            <div className="text-sm text-slate-300 font-mono">Total ${discounted.toFixed(2)}</div>
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            <button onClick={add} data-testid="vr-add-to-cart"
              className="flex-1 min-w-[150px] bg-cyan-400 text-black font-semibold rounded-xl px-4 py-3 hover:bg-cyan-300 transition flex items-center justify-center gap-2">
              Add to Lab Cart <ArrowRight size={16} />
            </button>
            <button onClick={() => onAsk && onAsk(p.peptide_slug || slug)} data-testid="vr-ask-scientist"
              className="rounded-xl px-4 py-3 border border-violet-400/40 text-violet-200 hover:bg-violet-500/10 transition flex items-center gap-2">
              <MessageSquare size={15} /> Ask Scientist
            </button>
            <button onClick={() => onFav && onFav(slug)}
              className={`rounded-xl px-4 py-3 border transition flex items-center gap-2 ${faved ? "border-pink-400 text-pink-300 bg-pink-500/10" : "border-white/15 text-slate-300 hover:border-pink-400/50"}`}>
              <Heart size={15} fill={faved ? "currentColor" : "none"} />
            </button>
          </div>

          {p.coas?.length > 0 && (
            <div className="mt-4 flex items-center gap-2 text-emerald-300 text-xs font-mono">
              <ShieldCheck size={14} /> {p.coas.length} verified COA{p.coas.length > 1 ? "s" : ""} on file
            </div>
          )}
        </div>
      </div>
    </Shell>
  );
}

function Shell({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="vr-inspect-panel">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-4xl rounded-3xl bg-[#080c12] border border-cyan-400/20 shadow-2xl overflow-hidden">
        <button onClick={onClose} className="absolute top-3 right-3 z-10 p-2 rounded-full bg-black/50 text-slate-300 hover:text-white" data-testid="vr-inspect-close"><X size={18} /></button>
        {children}
      </div>
    </div>
  );
}
